import React from 'react';

/**
 * Error Boundary Component
 * Catches React rendering errors and displays a fallback UI
 * Prevents entire app from going blank on errors
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, errorInfo: null };
  }

  static getDerivedStateFromError(error) {
    // Auto-recover from stale lazy-chunk fetches. After a deploy, the old
    // chunk filenames are gone from the CDN — when the user navigates to a
    // lazy-loaded route (e.g. Order History, KDS), React throws a
    // `ChunkLoadError`. Showing a scary error to the user is the wrong UX;
    // the correct move is a one-time hard reload so they pick up the new
    // bundle hashes. Guard with sessionStorage so we never infinite-loop
    // if the chunk is genuinely missing.
    try {
      const isChunkError =
        error &&
        (error.name === 'ChunkLoadError' ||
          /Loading chunk \d+ failed/i.test(error.message || '') ||
          /Loading CSS chunk/i.test(error.message || '') ||
          /Failed to fetch dynamically imported module/i.test(error.message || ''));
      if (isChunkError) {
        const flag = 'ff_chunk_reload_at';
        const last = parseInt(sessionStorage.getItem(flag) || '0', 10);
        const now = Date.now();
        if (!last || now - last > 60_000) {
          sessionStorage.setItem(flag, String(now));
          // Force the browser to bypass cache when fetching the new index.html.
          window.location.reload(true);
        }
      }
    } catch { /* never crash the error boundary itself */ }
    // Update state so the next render will show the fallback UI
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Log the error to console
    console.error('🚨 ErrorBoundary caught an error:', error);
    console.error('Component stack:', errorInfo?.componentStack);
    
    this.setState({
      error: error,
      errorInfo: errorInfo
    });
  }

  handleReload = () => {
    // Clear any cached data that might be causing issues
    try {
      localStorage.removeItem('pos_settings');
      sessionStorage.clear();
    } catch (e) {
      console.error('Error clearing storage:', e);
    }
    window.location.reload();
  };

  handleGoBack = () => {
    window.history.back();
  };

  handleGoHome = () => {
    window.location.href = '/';
  };

  handleGoToPOSLogin = () => {
    // Clear POS-related storage
    try {
      localStorage.removeItem('pos_token');
      localStorage.removeItem('pos_user');
      localStorage.removeItem('restaurant_id');
      localStorage.removeItem('pos_settings');
    } catch (e) {
      console.error('Error clearing storage:', e);
    }
    window.location.href = '/login';
  };

  render() {
    if (this.state.hasError) {
      // Fallback UI
      return (
        <div className="min-h-screen bg-gray-900 flex items-center justify-center p-4">
          <div className="max-w-md w-full bg-gray-800 rounded-xl p-8 text-center border border-red-500/30">
            {/* Error Icon */}
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <svg className="w-8 h-8 text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
              </svg>
            </div>
            
            <h2 className="text-xl font-bold text-white mb-2">
              Something went wrong
            </h2>
            
            <p className="text-gray-400 mb-6 text-sm">
              An unexpected error occurred. This has been logged and we will look into it.
            </p>

            {/* Error details - always show to help debug */}
            {this.state.error && (
              <div className="mb-6 p-3 bg-gray-900 rounded-lg text-left overflow-auto max-h-60">
                <p className="text-red-400 text-xs font-mono break-all mb-2">
                  <strong>Error:</strong> {this.state.error.toString()}
                </p>
                {this.state.errorInfo?.componentStack && (
                  <div className="text-gray-500 text-xs font-mono break-all">
                    <strong>Full Stack:</strong>
                    <pre className="mt-1 whitespace-pre-wrap text-[10px]">
                      {this.state.errorInfo.componentStack.split('\n').slice(0, 10).join('\n')}
                    </pre>
                  </div>
                )}
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={this.handleReload}
                className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white rounded-lg transition-colors font-medium"
              >
                🔄 Reload Page
              </button>
              
              <button
                onClick={this.handleGoToPOSLogin}
                className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                🔐 Go to POS Login
              </button>
              
              <button
                onClick={this.handleGoHome}
                className="w-full px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
              >
                🏠 Go to Home
              </button>
            </div>

            <p className="mt-6 text-gray-500 text-xs">
              If this problem persists, try clearing your browser cache or contact support.
            </p>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
